import { useEffect, useRef, useState } from 'react'
import {
  fetchInvites,
  fetchOrganizationMembers,
  removeOrganizationMember,
  revokeInvite,
} from '../../api/ws'
import * as api from '../../api/client'
import { useAppStore } from '../../store/app'
import type { InviteSummary, OrganizationMember } from '../../store/types'

// Temporary CRUD view for org members + their pending invitations.
// Members: list + per-row Remove (hard delete + cascade). Role/rename
// omitted (no WS surface yet).
// Invites: list every invite for the org, show status, allow Revoke
// on pending rows. Accepted/revoked/expired rows render as read-only
// history.
//
// Reachable via gear → About → Debug → "Members".
export function OrganizationMembersSheet() {
  const members = useAppStore((s) => s.organizationMembers)
  const invites = useAppStore((s) => s.invites)
  const orgs = useAppStore((s) => s.availableOrganizations)
  const currentOrg = orgs.find((o) => o.is_current)
  const [error, setError] = useState<string | null>(null)
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteSending, setInviteSending] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [inviteSentTo, setInviteSentTo] = useState<string | null>(null)
  const inviteInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (currentOrg?.id) {
      fetchOrganizationMembers(currentOrg.id)
      fetchInvites(currentOrg.id)
    }
  }, [currentOrg?.id])

  function handleError(e: unknown) {
    setError(typeof e === 'string' ? e : (e as Error)?.message ?? String(e))
  }

  function refresh() {
    if (currentOrg?.id) {
      fetchOrganizationMembers(currentOrg.id)
      fetchInvites(currentOrg.id)
    }
  }

  function openInviteForm() {
    setShowInviteForm(true)
    setInviteEmail('')
    setInviteError(null)
    setInviteSentTo(null)
    setTimeout(() => inviteInputRef.current?.focus(), 50)
  }

  async function sendInvite() {
    if (!currentOrg) return
    const trimmed = inviteEmail.trim().toLowerCase()
    if (!trimmed || inviteSending) return
    setInviteSending(true)
    setInviteError(null)
    setInviteSentTo(null)
    try {
      await api.sendInvite({ organizationID: currentOrg.id, email: trimmed })
      setInviteSentTo(trimmed)
      setInviteEmail('')
      refresh()
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : String(err))
    } finally {
      setInviteSending(false)
    }
  }

  if (!currentOrg) {
    return (
      <div style={{ padding: '12px 16px', fontSize: 14 }}>
        <p style={{ opacity: 0.6 }}>No current household.</p>
      </div>
    )
  }

  return (
    <div style={{ padding: '12px 16px', fontSize: 14 }}>
      {error && (
        <div
          style={{
            padding: '8px 12px',
            background: '#fee',
            color: '#900',
            borderRadius: 4,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      <h3 style={{ margin: '4px 0 4px', fontSize: 13, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Members
      </h3>
      {members.length === 0 ? (
        <p style={{ opacity: 0.6 }}>No members in this household.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {members.map((m) => (
            <MemberRow
              key={m.human_user_id}
              member={m}
              orgID={currentOrg.id}
              onError={handleError}
              onClearError={() => setError(null)}
              onMutated={refresh}
            />
          ))}
        </ul>
      )}

      {!showInviteForm ? (
        <button
          onClick={openInviteForm}
          style={{ marginTop: 12, fontSize: 14, color: 'var(--tint)' }}
        >
          + Invite Member
        </button>
      ) : (
        <div style={{ marginTop: 12, padding: '12px', background: 'var(--bg-grouped-cell)', borderRadius: 8 }}>
          <input
            ref={inviteInputRef}
            type="email"
            inputMode="email"
            autoCapitalize="off"
            autoCorrect="off"
            placeholder="name@example.com"
            value={inviteEmail}
            disabled={inviteSending}
            onChange={(e) => { setInviteEmail(e.target.value); setInviteError(null); setInviteSentTo(null) }}
            onKeyDown={(e) => { if (e.key === 'Enter') void sendInvite() }}
            style={{ width: '100%', fontSize: 16, padding: '8px 0', background: 'transparent', border: 'none', borderBottom: '1px solid var(--separator)', outline: 'none', color: 'var(--label)' }}
          />
          {inviteError && <p style={{ color: 'var(--danger)', fontSize: 13, margin: '6px 0 0' }}>{inviteError}</p>}
          {inviteSentTo && <p style={{ color: 'var(--success)', fontSize: 13, margin: '6px 0 0' }}>✓ Sent to {inviteSentTo}</p>}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button
              onClick={() => void sendInvite()}
              disabled={inviteSending || inviteEmail.trim() === ''}
              style={{ fontSize: 14, color: '#fff', background: 'var(--tint)', padding: '6px 14px', borderRadius: 6 }}
            >
              {inviteSending ? 'Sending…' : 'Send Invite'}
            </button>
            <button
              onClick={() => setShowInviteForm(false)}
              style={{ fontSize: 14, opacity: 0.6 }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <h3 style={{ margin: '20px 0 4px', fontSize: 13, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Pending Invitations
      </h3>
      {invites.length === 0 ? (
        <p style={{ opacity: 0.6 }}>No pending invitations.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {invites.map((inv) => (
            <InviteRow
              key={inv.id}
              invite={inv}
              onError={handleError}
              onClearError={() => setError(null)}
              onMutated={refresh}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function MemberRow({
  member,
  orgID,
  onError,
  onClearError,
  onMutated,
}: {
  member: OrganizationMember
  orgID: string
  onError: (e: unknown) => void
  onClearError: () => void
  onMutated: () => void
}) {
  const [busy, setBusy] = useState(false)

  async function handleRemove() {
    const who = member.email || member.name || member.human_user_id
    if (
      !confirm(
        `Remove "${who}" from this household?\n\n` +
          'This will: retire their agents in this org, abandon their ' +
          'working directories, and revoke their terminal devices. ' +
          'There is no undo.',
      )
    ) {
      return
    }
    onClearError()
    setBusy(true)
    try {
      const resp = await removeOrganizationMember({
        organizationID: orgID,
        humanUserID: member.human_user_id,
      })
      if (resp?.error) {
        onError(resp.error)
        return
      }
      onMutated()
    } catch (e) {
      onError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <li
      style={{
        padding: '12px 0',
        borderBottom: '1px solid #eee',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <div style={{ fontWeight: 600 }}>{member.name || member.email || member.human_user_id}</div>
        <div style={{ opacity: 0.6, fontSize: 12 }}>{member.role}</div>
      </div>
      {member.email && member.name && (
        <div style={{ opacity: 0.7, fontSize: 12 }}>{member.email}</div>
      )}
      <div>
        <button
          onClick={handleRemove}
          disabled={busy}
          style={{ color: '#900' }}
        >
          Remove
        </button>
      </div>
    </li>
  )
}

function InviteRow({
  invite,
  onError,
  onClearError,
  onMutated,
}: {
  invite: InviteSummary
  onError: (e: unknown) => void
  onClearError: () => void
  onMutated: () => void
}) {
  const [busy, setBusy] = useState(false)
  const pending = invite.status === 'pending'

  async function handleRevoke() {
    if (!confirm(`Revoke invite to "${invite.invited_email}"?\n\nThe link in any prior email stops working.`)) {
      return
    }
    onClearError()
    setBusy(true)
    try {
      const resp = await revokeInvite({ id: invite.id })
      if (resp?.error) {
        onError(resp.error)
        return
      }
      onMutated()
    } catch (e) {
      onError(e)
    } finally {
      setBusy(false)
    }
  }

  const statusColor =
    invite.status === 'pending'
      ? '#0a0'
      : invite.status === 'accepted'
        ? '#06c'
        : '#888'

  return (
    <li
      style={{
        padding: '12px 0',
        borderBottom: '1px solid #eee',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <div style={{ fontWeight: 600 }}>{invite.invited_email}</div>
        <div style={{ fontSize: 12, color: statusColor, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {invite.status}
        </div>
      </div>
      <div style={{ opacity: 0.7, fontSize: 12 }}>
        expires {fmtDate(invite.expires_at)}
      </div>
      {pending && (
        <div>
          <button
            onClick={handleRevoke}
            disabled={busy}
            style={{ color: '#900' }}
          >
            Revoke
          </button>
        </div>
      )}
    </li>
  )
}

function fmtDate(s: string): string {
  const d = new Date(s)
  if (isNaN(d.getTime())) return s
  return d.toLocaleString()
}
